import { useSelector } from "react-redux";

import { regionCodes } from "common/static/regions";

import { getUserRegion } from "services/region";

import {
  useFetchConnectedParents,
  useFetchPendingParentConnections
} from "seneca-common/features/parents/features/student-parents/service/hooks/useFetchParentConnections";
import {
  getUserSessionsCompleted,
  userStatsFetchFinished
} from "seneca-common/features/stats/state";
import { premiumHasAccessToQuizMode } from "seneca-common/features/subscriptions/features/subscription/selectors";
import { subscriptionsFetchSucceeded } from "seneca-common/features/subscriptions/state";
import {
  getUserAccountType,
  getUserInfoFetchFinished,
  getUserManagedBy,
  isSignedIn
} from "seneca-common/features/user/state";
import useSelectorOnCondition from "seneca-common/utils/hooks/selectors/useSelectorOnCondition";

import { useIsPlurallApp } from "features/plurall";

export function useDarkModeGatingCriteria() {
  const userStatsFetched = useSelector(userStatsFetchFinished);

  const sessionsStudied = useSelectorOnCondition(
    userStatsFetched,
    getUserSessionsCompleted
  );

  const isPlurallApp = useIsPlurallApp();

  const subscriptionFetched = useSelector(subscriptionsFetchSucceeded);
  const userHasAppropriateSubscription = useSelectorOnCondition(
    subscriptionFetched,
    premiumHasAccessToQuizMode
  );

  const userInfoFetched = useSelector(getUserInfoFetchFinished);
  const userRegion = useSelectorOnCondition(userInfoFetched, getUserRegion);
  const userAccountType = useSelectorOnCondition(
    userInfoFetched,
    getUserAccountType
  );

  const managedBy = useSelectorOnCondition(userInfoFetched, getUserManagedBy);
  const isMisSynced = managedBy === "misSyncService";

  const isUserSignedIn = useSelector(isSignedIn);

  const {
    data: connectedParents,
    isInitialLoading: isConnectedParentFetchLoading
  } = useFetchConnectedParents({ enabled: isUserSignedIn && isMisSynced });

  const {
    data: pendingParentConnections,
    isInitialLoading: isPendingParentFetchLoading
  } = useFetchPendingParentConnections({
    enabled: isUserSignedIn && isMisSynced
  });

  const isLoading =
    (userAccountType === "schoolstudent" && !userStatsFetched) ||
    !userInfoFetched ||
    !subscriptionFetched ||
    isConnectedParentFetchLoading ||
    isPendingParentFetchLoading;

  const criteriaMet =
    userRegion !== regionCodes.GB ||
    userAccountType !== "schoolstudent" ||
    isPlurallApp ||
    userHasAppropriateSubscription ||
    (isMisSynced ? sessionsStudied! >= 100 : sessionsStudied! >= 25) ||
    (isMisSynced && connectedParents && connectedParents.length > 0);

  return {
    criteriaMet,
    isMisSynced,
    pendingParentConnections,
    sessionsStudied,
    isLoading
  };
}
